import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  CollectionPreferences,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  PropertyFilter,
  PropertyFilterProps,
} from "@amzn/awsui-components-react/polaris";
import Header from "@amzn/awsui-components-react/polaris/header";
import { useDebounce } from "./hooks";
import { useNotifications } from "features/notifications/hooks";
import EmptyState from "../empty-state";
import { getMatchesCountText } from "utils/table-utils";
import { getQueryErrorMessage } from "utils/error-message-utils";
import { intersection } from "lodash";
import {
  IServerSideTableWithPropertyFilterProps,
  UniqueValuesConfig,
} from "./definitions";
import { getFieldType, setsAreEqual } from "./utils";

export const ServerSideTableWithPropertyFilter: React.FC<
  IServerSideTableWithPropertyFilterProps
> = ({
  columnDefinitions,
  tableTitle,
  headerDescription,
  dataSource,
  preferencesConfig,
  propertyFilter,
  autocomplete,
  externalFilterQuery,
  onSelectionChange,
  headerActions,
  aiQuerySlot,
  selectedItems: controlledSelectedItems,
  onQueryReady,
  onQueryChange,
  onDataChange,
  expandableRows,
  isItemDisabled,
  trackBy,
  hideHeader,
}) => {
  const { addNotification } = useNotifications();
  const errorRef = useRef<string | null>(null);

  // Extract values from grouped configs with defaults
  const {
    queryHook,
    transformParams,
    transformResponse,
    skipQuery = false,
  } = dataSource;

  const {
    saveWidths,
    preferences,
    setPreferences,
    contentDisplayPreferenceOptions,
    selectionType,
    defaultPageSize = 25,
    defaultSortingColumnIdx = 0,
    defaultSortingDescending = false,
    loadingText = "Loading data...",
    pageSizeOptions,
  } = preferencesConfig;

  const {
    showPropertyFilter = true,
    filteringProperties = [],
    propertyFilterI18nStrings,
    enableFreeTextSearch = true,
    freeTextSearchPlaceholder = "Search across all fields...",
    enableTokenSearch = true,
    staticFilteringOptions,
  } = propertyFilter || {};

  const { uniqueValuesQueryHook, uniqueValuesParams, uniqueValuesConfig } =
    autocomplete || {};
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [descendingSorting, setDescendingSorting] = useState(
    defaultSortingDescending,
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<any>
  >(columnDefinitions[defaultSortingColumnIdx]);
  const [searchAfterTokens, setSearchAfterTokens] = useState<
    Map<number, string[]>
  >(new Map());

  const [propertyFilterQuery, setPropertyFilterQuery] =
    useState<PropertyFilterProps.Query>({
      tokens: [],
      operation: "and",
    });

  // Listen for external filter changes and update internal state
  useEffect(() => {
    if (externalFilterQuery) {
      setPropertyFilterQuery(externalFilterQuery);
    }
  }, [externalFilterQuery]);

  // State to track which properties are visible and need autocomplete
  const [visibleProperties, setVisibleProperties] = useState<Set<string>>(
    new Set(),
  );

  // Track which properties we've already fetched from API to avoid re-fetching
  const [fetchedProperties, setFetchedProperties] = useState<Set<string>>(
    new Set(),
  );

  const [finalFilteringOptions, setFinalFilteringOptions] = useState<
    PropertyFilterProps.FilteringOption[]
  >([]);

  // field unique values loading configs
  const config: UniqueValuesConfig = useMemo(
    () => ({
      enabled: !!uniqueValuesQueryHook && !!uniqueValuesParams,
      skipFieldTypes: ["date", "numeric", "boolean", "freetext"],
      loadOnlyVisibleColumns: true,
      ...uniqueValuesConfig,
    }),
    [uniqueValuesQueryHook, uniqueValuesParams, uniqueValuesConfig],
  );

  // Debounce PropertyFilter changes to avoid too many API calls
  const debouncedPropertyFilterQuery = useDebounce(propertyFilterQuery, 300);

  const { pageSize } = preferences ?? {};

  const transformedParams = transformParams({
    pagination: {
      currentPageIndex,
      pageSize: pageSize || defaultPageSize,
      searchAfter: searchAfterTokens.get(currentPageIndex - 1),
    },
    sorting: {
      sortingColumn: {
        sortingField: sortingColumn.sortingField,
      },
      sortingDescending: descendingSorting,
    },
    propertyFiltering: {
      filteringQuery: debouncedPropertyFilterQuery,
    },
  });

  const queryResult = queryHook(transformedParams, {
    skip: skipQuery,
    refetchOnMountOrArgChange: true,
  });

  const { data, isLoading, error } = queryResult;
  const refetch = queryResult.refetch;

  // Expose refetch to parent component
  useEffect(() => {
    if (onQueryReady && refetch) {
      onQueryReady({ refetch });
    }
  }, [onQueryReady, refetch]);

  // Transform response
  const transformedData = data ? transformResponse(data) : null;
  const items = transformedData?.items || [];
  const totalCount = transformedData?.totalCount || 0;

  const pagesCount = Math.ceil(totalCount / (pageSize || defaultPageSize));

  // Expose current page data to parent via callback
  useEffect(() => {
    if (onDataChange && transformedData) {
      onDataChange({ items, totalCount });
    }
  }, [data]);

  // Calculate max page for direct navigation (OpenSearch 10k limit)
  // beyond this limit we must load prev page for cursor based pagination
  const MAX_OPENSEARCH_RESULTS = 10000;
  const maxDirectPage = Math.floor(
    MAX_OPENSEARCH_RESULTS / (pageSize || defaultPageSize),
  );
  const displayedPagesCount = Math.max(
    Math.min(pagesCount, maxDirectPage),
    currentPageIndex,
  );
  const hasMorePages =
    pagesCount > maxDirectPage && currentPageIndex < pagesCount;

  // Calculate which properties need to be fetched (visible but not yet fetched)
  const propertiesToFetch = useMemo(() => {
    const newProperties = Array.from(visibleProperties).filter(
      (prop) => !fetchedProperties.has(prop),
    );
    return newProperties.length > 0 ? newProperties : null;
  }, [visibleProperties, fetchedProperties]);

  // Call the unique values query hook only for new fields
  const uniqueValuesQueryParams = useMemo(() => {
    if (!uniqueValuesParams || !propertiesToFetch) {
      return null;
    }
    return {
      ...uniqueValuesParams,
      fields: propertiesToFetch,
    };
  }, [uniqueValuesParams, propertiesToFetch]);

  const { data: uniqueValuesData } = uniqueValuesQueryHook?.(
    uniqueValuesQueryParams!,
    { skip: !uniqueValuesQueryParams },
  ) || {
    data: undefined,
    error: undefined,
  };

  // Add new unique values to filtering options and mark as fetched
  useEffect(() => {
    if (!uniqueValuesData?.uniqueValues) {
      return;
    }

    const newValuesMap = uniqueValuesData.uniqueValues;
    const newOptions: PropertyFilterProps.FilteringOption[] = [];

    Object.entries(newValuesMap).forEach(([propertyKey, values]) => {
      const propertyOptions = values.map((value) => ({
        propertyKey,
        value,
      }));
      newOptions.push(...propertyOptions);
    });

    setFinalFilteringOptions((prev) => [...prev, ...newOptions]);

    setFetchedProperties((prev) => {
      const updated = new Set(prev);
      Object.keys(newValuesMap).forEach((key) => updated.add(key));
      return updated;
    });
  }, [uniqueValuesData]);

  // Serialize uniqueValuesParams for stable dependency comparison (avoids infinite resets from inline objects)
  const serializedUniqueValuesParams = JSON.stringify(uniqueValuesParams);

  // Reset fetched properties cache when unique values params change (e.g., folder navigation)
  // Use serialized value to avoid resetting on every render due to new object references
  useEffect(() => {
    if (
      serializedUniqueValuesParams &&
      serializedUniqueValuesParams !== "null"
    ) {
      setFetchedProperties(new Set());
      setFinalFilteringOptions([]);
    }
  }, [serializedUniqueValuesParams]);

  // load everything in filteringProperties except excluded field types
  useEffect(() => {
    if (
      !config.enabled ||
      !enableTokenSearch ||
      !uniqueValuesQueryHook ||
      !uniqueValuesParams
    ) {
      return;
    }

    // Load unique values for all filteringProperties except excluded field types
    const propertiesToLoad = new Set(
      filteringProperties
        .filter((property) => {
          const fieldType = getFieldType(property);
          // Only skip excluded field types (date, numeric, boolean, freetext)
          return !config.skipFieldTypes.includes(fieldType);
        })
        .map((property) => (property as any).key || ""),
    );

    if (!setsAreEqual(visibleProperties, propertiesToLoad)) {
      setVisibleProperties(propertiesToLoad);
    }
  }, [
    config.enabled,
    config.skipFieldTypes,
    enableTokenSearch,
    filteringProperties,
    uniqueValuesQueryHook,
    uniqueValuesParams,
    visibleProperties,
  ]);

  // Handle preference changes (column visibility)
  const handlePreferencesChange = useCallback(
    (newPreferences: any) => {
      setPreferences(newPreferences);
    },
    [setPreferences],
  );

  useEffect(() => {
    if (controlledSelectedItems !== undefined) {
      setSelectedItems(controlledSelectedItems);
    } else {
      setSelectedItems((oldSelected) => intersection(items, oldSelected));
    }
  }, [controlledSelectedItems, items.length, items[0]?.taskId]);

  // Store searchAfter tokens from API responses
  useEffect(() => {
    const searchAfter = transformedData?.paginationMetadata?.searchAfter;
    if (searchAfter && searchAfter.length > 0) {
      setSearchAfterTokens((prev) => {
        const existing = prev.get(currentPageIndex);
        if (existing && existing.join(",") === searchAfter.join(",")) {
          return prev;
        }
        const updated = new Map(prev);
        updated.set(currentPageIndex, searchAfter);
        return updated;
      });
    }
  }, [transformedData, currentPageIndex]);

  // Clear search tokens and reset to page 1 on filter change
  useEffect(() => {
    setCurrentPageIndex(1);
    setSearchAfterTokens(new Map());
  }, [debouncedPropertyFilterQuery]);

  // Clear search tokens on sort change
  useEffect(() => {
    setSearchAfterTokens(new Map());
  }, [sortingColumn, descendingSorting]);

  useEffect(() => {
    setCurrentPageIndex(1);
    setSearchAfterTokens(new Map());
  }, [pageSize]);

  const onSortingChange: TableProps["onSortingChange"] = (event) => {
    setDescendingSorting(event.detail.isDescending ?? false);
    setSortingColumn(event.detail.sortingColumn);
    setCurrentPageIndex(1);
  };

  const handleSelectionChange = useCallback(
    (event: any) => {
      const newSelectedItems = event.detail.selectedItems;
      if (onSelectionChange) {
        const result = onSelectionChange(newSelectedItems);
        // If callback returns an array, use it as the selection
        if (Array.isArray(result)) {
          setSelectedItems(result);
          return;
        }
      }
      setSelectedItems(newSelectedItems);
    },
    [onSelectionChange],
  );

  const handlePropertyFilterChange = useCallback(
    ({ detail }: { detail: PropertyFilterProps.Query }) => {
      setPropertyFilterQuery(detail);
      onQueryChange?.(detail);
    },
    [onQueryChange],
  );

  useEffect(() => {
    if (error) {
      const errorMessage = getQueryErrorMessage(
        error,
        "Failed to submit the request for querying items. Please contact Gaia team for issues.",
      );
      if (errorMessage !== errorRef.current) {
        errorRef.current = errorMessage;
        addNotification({
          type: "error",
          header: errorMessage,
          dismissible: true,
          timeout: 10000,
          id: `error-${errorMessage}-${Date.now()}`,
        });
      }
    }
  }, [error, addNotification]);

  useEffect(() => {
    if (!error && !isLoading && errorRef.current) {
      errorRef.current = null;
    }
  }, [error, isLoading]);

  const mergedFilteringOptions = useMemo(
    () => [...(staticFilteringOptions ?? []), ...finalFilteringOptions],
    [staticFilteringOptions, finalFilteringOptions],
  );

  const propertyFilterProps = useMemo(
    (): PropertyFilterProps => ({
      query: propertyFilterQuery,
      onChange: handlePropertyFilterChange,

      filteringProperties: enableTokenSearch ? filteringProperties : [],
      filteringOptions: enableTokenSearch ? mergedFilteringOptions : [],

      ...(enableFreeTextSearch && {
        freeTextFiltering: {
          operators: [":"], // Default to "contains" operator for free text
          defaultOperator: ":",
        },
      }),

      ...(propertyFilterI18nStrings && {
        i18nStrings: {
          ...propertyFilterI18nStrings,
          ...(freeTextSearchPlaceholder && {
            filteringPlaceholder: freeTextSearchPlaceholder,
          }),
        },
      }),

      // UI configuration
      enableTokenGroups: true,
      expandToViewport: true,
      countText: getMatchesCountText(totalCount),
    }),
    [
      propertyFilterQuery,
      handlePropertyFilterChange,
      enableTokenSearch,
      filteringProperties,
      mergedFilteringOptions,
      enableFreeTextSearch,
      propertyFilterI18nStrings,
      freeTextSearchPlaceholder,
      totalCount,
    ],
  );

  return (
    <Table
      enableKeyboardNavigation={true}
      {...(expandableRows && { expandableRows })}
      {...(isItemDisabled && { isItemDisabled })}
      {...(trackBy && { trackBy })}
      loading={isLoading}
      selectedItems={selectedItems}
      items={items}
      onSortingChange={onSortingChange}
      onSelectionChange={handleSelectionChange}
      sortingColumn={sortingColumn}
      sortingDescending={descendingSorting}
      columnDefinitions={columnDefinitions}
      columnDisplay={preferences?.contentDisplay}
      selectionType={selectionType}
      variant="full-page"
      stickyHeader={true}
      resizableColumns={true}
      onColumnWidthsChange={saveWidths}
      wrapLines={preferences?.wrapLines}
      stripedRows={preferences?.stripedRows}
      contentDensity={preferences?.contentDensity}
      stickyColumns={preferences?.stickyColumns}
      header={
        hideHeader ? undefined : (
          <Header
            counter={
              !isLoading
                ? selectedItems.length > 0
                  ? `(${selectedItems.length}/${totalCount})`
                  : `(${totalCount})`
                : undefined
            }
            actions={headerActions}
            description={headerDescription}
          >
            {tableTitle}
          </Header>
        )
      }
      loadingText={loadingText}
      empty={
        error ? (
          <EmptyState
            title="Error loading data"
            subtitle={getQueryErrorMessage(
              error,
              "An unexpected error occurred. Please try again.",
            )}
            action={<></>}
          />
        ) : (
          <EmptyState
            title="No matches"
            subtitle="We can't find a match."
            action={<></>}
          />
        )
      }
      filter={
        showPropertyFilter ? (
          <SpaceBetween size="l">
            {aiQuerySlot}
            <PropertyFilter {...propertyFilterProps} />
          </SpaceBetween>
        ) : aiQuerySlot ? (
          <SpaceBetween size="l">{aiQuerySlot}</SpaceBetween>
        ) : undefined
      }
      pagination={
        <Pagination
          pagesCount={displayedPagesCount}
          currentPageIndex={currentPageIndex}
          disabled={isLoading}
          openEnd={hasMorePages}
          onChange={(event) => {
            const targetPage = event.detail.currentPageIndex;
            if (targetPage <= pagesCount) {
              setCurrentPageIndex(targetPage);
            }
          }}
        />
      }
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          disabled={false}
          preferences={preferences}
          onConfirm={({ detail }) => handlePreferencesChange(detail)}
          pageSizePreference={{
            options: pageSizeOptions || [
              { value: 10, label: "10 items" },
              { value: 25, label: "25 items" },
              { value: 50, label: "50 items" },
              { value: 100, label: "100 items" },
            ],
            title: "Page size",
          }}
          contentDisplayPreference={{
            options: contentDisplayPreferenceOptions || [],
            title: "Column preference",
          }}
          wrapLinesPreference={{
            label: "Wrap lines",
            description: "Select to see all the text and wrap the lines",
          }}
          stripedRowsPreference={{
            label: "Striped rows",
            description: "Select to shade every other row",
          }}
          contentDensityPreference={{
            label: "Compact mode",
            description: "Select to display compact mode",
          }}
          stickyColumnsPreference={{
            firstColumns: {
              title: "Stick first column(s)",
              description:
                "Keep the first column(s) visible while horizontally scrolling the table content.",
              options: [
                { label: "None", value: 0 },
                { label: "First column", value: 1 },
                { label: "First two columns", value: 2 },
              ],
            },
            lastColumns: {
              title: "Stick last column",
              description:
                "Keep the last column visible while horizontally scrolling the table content.",
              options: [
                { label: "None", value: 0 },
                { label: "Last column", value: 1 },
                { label: "Last two columns", value: 2 },
              ],
            },
          }}
        />
      }
    />
  );
};
